import React, { useState, useImperativeHandle } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactDollarField from 'shared/design-system/components/TrueImpactDollarField';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import ProgramCostModal from './ProgramCostModal';
import { BottomBar } from '../BottomBar';
import {
  BudgeComponentProps,
  BudgetDollarComponent,
  BudgetSubmitReference,
  defaultCostList,
  DIRECT_COST_QUESTION_ID,
} from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import RelevancyCheckbox, {
  defaultCostValue,
  isCostValid,
} from './RelevancyCheckbox';
import ShowGuidanceText from '../Review/ReportCustomQuestionType/ShowGuidanceText';

export const directCostCheckboxText =
  'Direct Cost is not relevant to my program';

const StyledDiv = styled.div`
  .question-text {
    max-width: 590px;
    margin-top: 48px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
  .sub-heading {
    max-width: 502px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.large};
  }
  .calculate-cost {
    margin: ${SPACERS.small} auto 0;
  }
  .cost-field {
    max-width: 192px;
    margin: 0 auto;
  }
`;

export const HelperContainer = styled.div`
  max-width: 590px;
  border-radius: ${SPACERS.borderRadiusMain};
  background-color: ${COLORS.greyLightest};
  margin: 0 auto;
  padding: ${SPACERS.medium};
`;

export const CostContainer = styled.div`
  margin: 0 auto ${SPACERS.large};
`;

const ProgramCost = React.forwardRef<
  BudgetSubmitReference,
  BudgeComponentProps & BudgetDollarComponent
>(
  (
    {
      guidanceText,
      reportId,
      initialValue,
      hideBottomBar,
      isReportPassed,
      onNext,
      onPrevious,
    },
    forwardRef,
  ) => {
    const [cost, setCost] = useState<string>(initialValue);
    const [open, setOpen] = useState<boolean>(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleNext = () => onNext(cost);

    useImperativeHandle(forwardRef, () => ({
      handleSubmit: () => JSON.stringify({ currency: cost }),
      error: () => (cost ? undefined : 'Please enter a valid cost.'),
    }));

    const guidance = guidanceText[DIRECT_COST_QUESTION_ID];
    return (
      <>
        <StyledDiv>
          <TrueImpactText variant="heading-page" className="question-text">
            {isReportPassed
              ? 'What was the direct cost of your program for this report year?'
              : 'What is the expected direct cost of your program for this report year?'}
          </TrueImpactText>
          <ShowGuidanceText
            sectionId="budget"
            questionId={DIRECT_COST_QUESTION_ID}
            guidanceText={guidanceText}
          />
          <TrueImpactText className="sub-heading" align="center">
            This includes staff, materials, travels, and other operating
            expenses, but it excludes in-kind donations and overhead costs.
          </TrueImpactText>
          <CostContainer>
            <TrueImpactDollarField
              value={cost}
              onChange={setCost}
              disabled={cost === defaultCostValue}
              toFixedDecimal={2}
              placeholder="Total direct cost"
              className="cost-field"
            />
            {cost === defaultCostValue && (
              <RelevancyCheckbox
                value={cost}
                handleValue={setCost}
                text={directCostCheckboxText}
              />
            )}
          </CostContainer>
          <HelperContainer>
            <TrueImpactText variant="heading-subheading" align="center">
              Want some help calculating direct cost?
            </TrueImpactText>
            <TrueImpactText variant="body" align="center">
              You don’t have to use this, but we’ve got a handy calculator just
              for this that will help.
            </TrueImpactText>
            <TrueImpactButton
              disabled={false}
              variant="secondary-large"
              onClick={handleOpen}
              className="calculate-cost"
            >
              Help me calculate this
            </TrueImpactButton>
          </HelperContainer>
        </StyledDiv>
        {!hideBottomBar && (
          <BottomBar
            disabled={false}
            hideBack={false}
            hideNext={!isCostValid(cost)}
            handleBack={onPrevious}
            handleNext={handleNext}
            backTitle="Back"
            nextTitle="Save & Continue"
          />
        )}
        <ProgramCostModal
          reportId={reportId}
          open={open}
          title="Calculate direct cost"
          defaultCostList={defaultCostList}
          setCost={setCost}
          handleClose={handleClose}
        />
      </>
    );
  },
);

export default ProgramCost;
